import React, { useRef, useState } from "react";
import { url } from "api";
import { Tag, Tooltip, Dropdown, message } from "antd";
import {
  EllipsisOutlined,
  FileImageOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { SettledBadge, settledStyle } from "helpers/useSettledRows";
import { T, STATUS_COLORS, PRIORITY_CHIPS } from "../theme";
import { dueChipLabel, formatDue, isPastDue, daysToDue } from "helpers/issueDueDate";
import { issueHref } from "helpers/issueHref";

// Board columns = statuses. "In review" (under_review) sits between Pending
// and Resolved so issues awaiting approval don't vanish from the board;
// resolved is the end of an issue (there is no separate "closed").
const COLUMNS = [
  { key: "open", label: "Open", dot: STATUS_COLORS.open, icon: <ClockCircleOutlined />, accepts: ["open"] },
  { key: "pending", label: "Pending", dot: STATUS_COLORS.pending, icon: <ExclamationCircleOutlined />, accepts: ["pending"] },
  // `shows` is what lands in the column, `accepts` is what may be
  // DROPPED into it - the same list everywhere except here. In review is
  // the review gate's state to set: an issue arrives when somebody
  // outside the job claims an ending and leaves when a reviewer answers.
  // Dragging a card in parked it in a queue that lists only submissions,
  // so there was nothing to approve and nothing to send back - the issue
  // simply stopped (prod SPX-314, 2026-08-28).
  { key: "under_review", label: "In review", dot: T.amber, icon: <ExclamationCircleOutlined />, shows: ["under_review"], accepts: [] },
  { key: "resolved", label: "Resolved", dot: STATUS_COLORS.resolved, icon: <CheckCircleOutlined />, accepts: ["resolved"] },
  { key: "abandoned", label: "Abandoned", dot: STATUS_COLORS.abandoned, icon: <StopOutlined />, accepts: ["abandoned"] },
];

const PRIORITY_STYLE = {
  urgent: { bg: PRIORITY_CHIPS.urgent.bg, fg: PRIORITY_CHIPS.urgent.fg },
  normal: { bg: PRIORITY_CHIPS.normal.bg, fg: PRIORITY_CHIPS.normal.fg },
  low: { bg: PRIORITY_CHIPS.low.bg, fg: PRIORITY_CHIPS.low.fg },
};

const SOURCE_LABEL = {
  docControl: { label: "2D", bg: T.wash, fg: T.navyD },
  modelViewer: { label: "3D", bg: "#F1E8FB", fg: "#7A4BBF" },
  blank: { label: "—", bg: "#F3F4F5", fg: "#526470" },
};

function PriPill({ priority }) {
  const p = PRIORITY_STYLE[priority] || PRIORITY_STYLE.normal;
  return (
    <span style={{
      background: p.bg, color: p.fg, padding: "2px 6px", borderRadius: 3,
      fontSize: 9.5, fontWeight: 600, letterSpacing: ".04em",
    }}>{(priority || "normal").toUpperCase()}</span>
  );
}

// Due-date chip. Red once the due day has passed, amber on the last day,
// plain otherwise — on a board the point of a due date is to be seen
// without opening the card, so it reads "3d late" / "Today" / "in 4d"
// rather than a bare date until the date is more than a week out.
function DuePill({ dueDate, status }) {
  if (!dueDate) return null;
  const settled = ["resolved", "abandoned"].includes(status);
  const late = !settled && isPastDue(dueDate);
  const soon = !settled && !late && daysToDue(dueDate) <= 1;
  // Once the work is off the table the date is history, not a deadline:
  // a resolved issue reads "SEP 14", never "10D LATE".
  const label = settled ? formatDue(dueDate, "MMM D") : dueChipLabel(dueDate);
  const c = late
    ? { bg: T.removedBg, fg: T.removed2 }
    : soon
      ? { bg: T.ambBg, fg: "#8A6112" }
      : { bg: "#F3F4F5", fg: "#526470" };
  return (
    <Tooltip title={`Due ${formatDue(dueDate, "MMMM D, YYYY")}`}>
      <span style={{
        background: c.bg, color: c.fg, padding: "2px 6px", borderRadius: 3,
        fontSize: 9.5, fontWeight: 600, letterSpacing: ".04em", whiteSpace: "nowrap",
      }}>
        <ClockCircleOutlined style={{ fontSize: 9, marginRight: 3 }} />
        {label.toUpperCase()}
      </span>
    </Tooltip>
  );
}

function SourceTag({ sourceType }) {
  const s = SOURCE_LABEL[sourceType] || SOURCE_LABEL.blank;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: "2px 5px", borderRadius: 3,
      fontSize: 9.5, fontWeight: 700, letterSpacing: ".04em",
    }}>{s.label}</span>
  );
}

function IssueCard({ issue, onOpen, menuItems, draggable, onDragStart, onDragEnd, dragging, busy }) {
  const responsibles = issue.responsibleUsers || [];
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        // Native HTML5 DnD: the payload is the issue id; the column decides
        // on drop. setData is required for Firefox to start a drag at all.
        try { e.dataTransfer.setData("text/plain", String(issue._id)); } catch (err) { /* ignore */ }
        e.dataTransfer.effectAllowed = "move";
        onDragStart && onDragStart(issue);
      }}
      onDragEnd={() => onDragEnd && onDragEnd()}
      style={{
        background: "#fff",
        border: `1px solid ${dragging ? T.navy : T.line}`,
        borderRadius: 6,
        padding: 10,
        boxShadow: dragging ? "0 6px 18px rgba(35,52,62,.18)" : "0 1px 1px rgba(0,0,0,.02)",
        cursor: draggable ? "grab" : "pointer",
        opacity: busy ? 0.55 : (dragging ? 0.6 : 1),
        transition: "box-shadow .12s, opacity .12s",
        userSelect: "none",
      }}
      onClick={onOpen}
      title={draggable ? "Drag to another column to change the status - click to open" : "Click to open"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <SourceTag sourceType={issue.sourceType} />
        {/* A real link, so right-click / middle-click open the issue in a
            new tab; the card's own click opens it the same way. */}
        <a href={issueHref(issue)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
           style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {issue._id?.slice(-6) || "—"}
        </a>
        <PriPill priority={issue.priority} />
      </div>

      {issue.dueDate && (
        <div style={{ marginBottom: 6 }}>
          <DuePill dueDate={issue.dueDate} status={issue.status} />
        </div>
      )}

      {(issue.thumbnail || issue.sourceModelViewer?.screenshot || issue.firstElementShot) && (
        <div style={{
          width: "100%", height: 80, borderRadius: 4, overflow: "hidden",
          background: "#F3F4F5", marginBottom: 8,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <img src={(() => { const p = issue.thumbnail || issue.sourceModelViewer?.screenshot || issue.firstElementShot; return p && p.startsWith("/") ? `${url}${p}` : p; })()} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      )}

      <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, marginBottom: 8, lineHeight: 1.35, ...settledStyle(issue) }}>
        {issue.title || "Untitled"}
        <SettledBadge row={issue} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, minHeight: 16 }}>
        <span style={{ fontSize: 10.5, color: T.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {issue.projectId?.title || "—"}
        </span>
        <span style={{ fontSize: 10.5, color: T.sub }}>
          {moment(issue.updatedAt).fromNow()}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {responsibles.length > 0 ? (
          <Tooltip title={responsibles.map(u => u.name).join(", ")}>
            <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {responsibles.slice(0, 3).map((u, idx) => (
                <div key={u._id || idx} style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: T.navy, color: "#fff",
                  fontSize: 10, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  border: "1.5px solid #fff", marginLeft: idx === 0 ? 0 : -6,
                }}>
                  {(u.name || u.email || "?").charAt(0).toUpperCase()}
                </div>
              ))}
              {responsibles.length > 3 && (
                <span style={{ fontSize: 10, color: T.muted, marginLeft: 4 }}>
                  +{responsibles.length - 3}
                </span>
              )}
            </div>
          </Tooltip>
        ) : (
          <span style={{ fontSize: 10, color: T.sub }}>No assignees</span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10.5, color: T.muted }}>
          ◆ {issue.issueObjectsCount || 0}
        </span>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} onClick={e => e.stopPropagation()}>
          <button
            onClick={e => e.stopPropagation()}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2 }}
          >
            <EllipsisOutlined style={{ fontSize: 14, color: T.muted }} />
          </button>
        </Dropdown>
      </div>
    </div>
  );
}

// onStatusChange(issue, newStatus) is the page's handler (PUT
// /issue/:id/status + local merge). canDrag gates the whole DnD surface
// (issueEdit permission on the page).
export default function IssueKanbanView({ issues, onOpenViewer, getActionMenuItems, apiLoading, onStatusChange, canDrag = true }) {
  const shownBy = (c) => c.shows || c.accepts;
  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.key] = (issues || []).filter(m => shownBy(c).includes(m.status || "open"));
    return acc;
  }, {});
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const dragIssueRef = useRef(null);
  const draggable = !!(canDrag && onStatusChange);

  const startDrag = (issue) => { dragIssueRef.current = issue; setDragId(issue._id); };
  const endDrag = () => { dragIssueRef.current = null; setDragId(null); setOverCol(null); };
  const dropOn = async (col, e) => {
    e.preventDefault();
    const issue = dragIssueRef.current
      || (issues || []).find((m) => String(m._id) === String(e.dataTransfer?.getData("text/plain") || ""));
    endDrag();
    if (!issue) return;
    if (shownBy(col).includes(issue.status || "open")) return; // same column
    if (!col.accepts.length) {
      message.info("An issue goes into review when someone claims it is finished - it cannot be moved there by hand");
      return;
    }
    setBusyId(issue._id);
    try {
      await onStatusChange(issue, col.key);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      padding: "16px 24px",
      display: "grid",
      gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(0, 1fr))`,
      gap: 12,
      alignItems: "start",
    }}>
      {COLUMNS.map(col => {
        const rows = byStatus[col.key] || [];
        const dragging = dragIssueRef.current;
        const isTarget = draggable && dragId && overCol === col.key && dragging
          && col.accepts.length && !shownBy(col).includes(dragging.status || "open");
        return (
          <div key={col.key}
            onDragOver={(e) => { if (!draggable || !dragId) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overCol !== col.key) setOverCol(col.key); }}
            onDragEnter={(e) => { if (!draggable || !dragId) return; e.preventDefault(); setOverCol(col.key); }}
            onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; if (overCol === col.key) setOverCol(null); }}
            onDrop={(e) => draggable && dropOn(col, e)}
            style={{
              background: isTarget ? "#EEF5FB" : T.bg,
              border: `1px ${isTarget ? "dashed" : "solid"} ${isTarget ? T.navy : T.line}`,
              borderRadius: 6,
              display: "flex",
              flexDirection: "column",
              minHeight: 200,
              maxHeight: "calc(100 * var(--vh) - 260px)",
              overflow: "hidden",
              transition: "background .12s, border-color .12s",
            }}>
            <div style={{
              padding: "10px 12px",
              borderBottom: `1px solid ${T.line}`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
            }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.dot }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.navyD, textTransform: "uppercase", letterSpacing: ".05em" }}>{col.label}</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: T.muted, marginLeft: "auto" }}>
                {rows.length}
              </span>
            </div>
            <div style={{
              padding: 8, overflowY: "auto",
              display: "flex", flexDirection: "column", gap: 8, flex: 1,
            }}>
              {rows.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "24px 8px",
                  color: T.sub, fontSize: 11, fontStyle: "italic",
                }}>
                  {apiLoading ? "Loading…" : "No issues"}
                </div>
              ) : (
                rows.map(m => (
                  <IssueCard
                    key={m._id}
                    issue={m}
                    onOpen={() => { if (dragId) return; onOpenViewer(m); }}
                    menuItems={getActionMenuItems(m)}
                    draggable={draggable}
                    onDragStart={startDrag}
                    onDragEnd={endDrag}
                    dragging={dragId === m._id}
                    busy={busyId === m._id}
                  />
                ))
              )}
              {isTarget && (
                <div style={{ border: `1px dashed ${T.navy}`, borderRadius: 6, padding: "10px 8px", textAlign: "center", fontSize: 11, color: T.navyD, background: "#fff" }}>
                  Drop to move to {col.label}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
